import {
  PageProps,
  WrapPageElementBrowserArgs,
  WrapPageElementNodeArgs,
} from 'gatsby';
import React, {Suspense, useEffect, useMemo, useRef} from 'react';
import ModalRoutingContext from '../plugins/layout-wrapper/ModalRoutingContext';
import Modal from './components/general/PageModal/PageModal';
import Home from './components/page-template/Home/Home';
import TranslationLoader from './Providers/TranslationLoader/TranslationLoader';
import EmptyLayout from './layout/EmptyLayout/EmptyLayout';
import useNavigation from './Navigation/useNavigation';
import {useTrackingContext} from './Providers/Tracking/TrackingContext';
import {TraceTokEvents} from '@src/Tracking/catalog';
import {trackEvent} from '@src/Tracking/trackEvent';
import {consumeSignupPending} from '@src/Tracking/utils/signupPending';
import {isProbePath} from '@src/Tracking/utils/trackablePath';
import {setRouteIsNotFound} from '@src/Tracking/utils/analyticsRouteState';
import {
  derivePageGroup,
  derivePageName,
} from './Tracking/utils/enrich';
import useMember from './Store/selectors/useMember';
const SessionAlertProvider = React.lazy(
  () => import('./Providers/SessionProvider/SessionAlert/SessionAlertProvider'),
);

const ThankYouModal = React.lazy(
  () => import('./App/components/Common/ThankYouModal/ThankYouModal'),
);

export type ILayoutProviderProps = {
  element: WrapPageElementBrowserArgs['element'];
  props:
    | WrapPageElementBrowserArgs<
        any,
        any,
        {modal?: boolean} & Record<string, any>
      >['props']
    | WrapPageElementNodeArgs<any>['props'];
  locationState?: WrapPageElementBrowserArgs<
    any,
    any,
    {modal?: boolean} & Record<string, any>
  >['props']['location']['state'];
};

export type InnerLayoutProps = {
  context: Record<string, unknown>;
  data?: Record<string, unknown>;
  params?: Record<string, unknown>;
  location?: PageProps['location'];
  isModal?: boolean;
};

const layouts = {
  AppLayout: React.lazy(() => import('./layout/AppLayout/AppLayout')),
  AwardLayout: React.lazy(() => import('./layout/AwardLayout/AwardLayout')),
  DefaultLayout: React.lazy(
    () => import('./layout/DefaultLayout/DefaultLayout'),
  ),
  EmptyLayout,
};

type TLayoutList = keyof typeof layouts;

const getElementLayout = (
  element?: React.ReactElement<any, string | React.JSXElementConstructor<any>>,
): TLayoutList => {
  let layout: TLayoutList = 'DefaultLayout';

  if (
    element &&
    typeof element.type !== 'string' &&
    // @ts-ignore
    element.type.layoutComponent
  ) {
    // @ts-ignore
    layout = element.type.layoutComponent;
  }

  if (
    element &&
    element?.type &&
    typeof element.type !== 'string' &&
    // @ts-ignore
    element?.type?.type &&
    // @ts-ignore
    typeof element?.type?.type !== 'string' &&
    // @ts-ignore
    element.type.type.layoutComponent
  ) {
    // @ts-ignore
    layout = element.type.type.layoutComponent;
  }

  return layout;
};

const getElementUri = (
  element?: React.ReactElement<any, string | React.JSXElementConstructor<any>>,
) => {
  let uri: string = element?.props?.uri + '';

  if (!uri) {
    return '/';
  }

  const {
    pageContext: {language},
  } = element?.props || {};

  if (language) {
    uri = uri.replace(new RegExp(`^/${language}|/${language}(?=/)`, 'g'), '');
    uri = uri === '' ? '/' : uri.startsWith('/') ? uri : `/${uri}`;
  }
  return uri;
};

/**
 * True when Gatsby rendered the 404 page for the current URL.
 *
 * Read defensively at two nesting levels, mirroring getElementLayout above:
 * gatsby-plugin-react-i18next wraps page components, so the marker can sit on
 * either `element.type` or `element.type.type` depending on the wrapper order.
 * If neither is present we return false and tracking behaves exactly as it did
 * before — the guard fails open, so a framework change can never silently
 * suppress real page views.
 */
const getElementIsNotFound = (
  element?: React.ReactElement<any, string | React.JSXElementConstructor<any>>,
): boolean => {
  const type: any = element?.type;
  if (!type || typeof type === 'string') return false;
  return Boolean(type.isNotFound || type?.type?.isNotFound);
};

/** Strip the locale prefix (/en, /fr, /pt) and the /app base from a pathname. */
const toRoute = (path: string): string =>
  (path || '/')
    .replace(/^\/[a-z]{2}(?=\/|$)/, '')
    .replace(/^\/app/, '') || '/';

/**
 * Query parameters that mark a genuine campaign / deeplink arrival.
 *
 * `pid` and `c` are what this app's own AppsFlyer OneLink builder actually
 * emits (utils/deepLinkUtils.ts:675-676) — AppsFlyer's media-source and
 * campaign parameters. Omitting them would have made real AppsFlyer traffic
 * emit nothing at all, which is worse than the mislabelling this replaced.
 * `af_source`/`af_campaign` are not produced anywhere in this repo but are kept
 * for inbound links built elsewhere; they cost nothing when absent.
 */
const CAMPAIGN_PARAMS = ['utm_campaign', 'campaign_id', 'af_campaign', 'c'];
const SOURCE_PARAMS = ['utm_source', 'af_source', 'pid'];

/** First non-empty value among `keys`, or undefined. Empty strings do not count. */
const firstParam = (
  params: URLSearchParams,
  keys: string[],
): string | undefined => {
  for (const key of keys) {
    const value = params.get(key);
    if (value) return value;
  }
  return undefined;
};

const LayoutProvider = (props: ILayoutProviderProps) => {
  const {element, props: wrapperProps} = props;
  const {emit, identify} = useTrackingContext();
  const member = useMember();
  const navigation = useNavigation();
  const initialLang = useRef(true);

  // Init product data
  const PreviousLayout = useRef<TLayoutList>();
  const PreviousElement =
    useRef<
      React.ReactElement<any, string | React.JSXElementConstructor<any>>
    >();

  const {pageContext, location} = wrapperProps;
  const locationState: {modal?: boolean; closeTo?: string} = useMemo(
    () =>
      (location.state as WrapPageElementBrowserArgs<
        any,
        any,
        {modal?: boolean} & Record<string, any>
      >['props']['location']['state']) || {},
    [location.state],
  );
  const isModal: boolean = useMemo(
    () =>
      // @ts-ignore
      !!(locationState && locationState.modal) ||
      // @ts-ignore
      element.type.isModal === true,
    [
      // @ts-ignore
      element.type.isModal,
      locationState,
    ],
  );
  const closeTo: string = useMemo(
    () =>
      PreviousElement.current
        ? getElementUri(PreviousElement.current)
        : (locationState && locationState.closeTo) || '/',
    [locationState, PreviousElement.current?.props?.uri],
  );
  const ParentContent: React.ComponentType = useMemo(
    () =>
      typeof element.type !== 'string' &&
      // @ts-ignore
      element.type.ParentContent
        ? // @ts-ignore
          element.type.ParentContent
        : Home,
    [element.type],
  );
  const PageElement = useMemo(
    () =>
      isModal
        ? PreviousElement.current ||
          // @ts-ignore
          React.createElement(ParentContent)
        : element,
    [ParentContent, element, isModal],
  );

  // Rendering the current page as a modal, so create an element with the page contents
  const modalElement = isModal ? element : null;

  const handleModalClose = () => {
    navigation.navigate(closeTo);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const elUri = useMemo(() => getElementUri(element), [element?.props?.uri]);

  // Does Gatsby's router consider this URL a 404? Derived during render from
  // the element it actually rendered, so it belongs to the SAME render pass as
  // `location` below. That pairing is the whole point — see the effect comment.
  const isNotFound = getElementIsNotFound(element);

  // Publish for consumers outside this subtree — BrazeProvider sits under
  // AppProvider and cannot see the page element, but must not open a Braze
  // session (which creates a profile) for a URL that isn't a real page.
  // Done in an effect, not during render: module-global mutation from a render
  // body is invalid under concurrent rendering, where renders can be discarded
  // or replayed.
  useEffect(() => {
    setRouteIsNotFound(isNotFound);
  }, [isNotFound]);

  // page_viewed (canonical).
  //
  // Keyed on Gatsby's own `location.pathname`, NOT a reach-router history
  // listener. This is a correctness requirement, not a style preference:
  //
  //   @gatsbyjs/reach-router `navigate` invokes history listeners SYNCHRONOUSLY
  //     r.forEach(function(e){return e({location:n,action:"PUSH"})})
  //   while Gatsby's LocationProvider defers the React update by a microtask
  //   AND an animation frame
  //     Promise.resolve().then(() => requestAnimationFrame(() => setState(...)))
  //
  // So anything derived from `element` — i.e. the 404 flag — is a full frame
  // behind inside such a listener. The previous implementation read a ref set
  // during render and was therefore always stale on SPA navigation: it
  // suppressed the first real page view after a 404, and let a page view
  // through when navigating INTO an extension-less 404.
  //
  // Taking both the path and the 404 flag from the same render makes them
  // impossible to desynchronise. `emit` is stable (AppTrackingProvider memoises
  // `adapters` with [], and TrackingContext memoises `emit`), so this effect
  // fires once per real navigation. TrackingContext's 50ms dedup remains as a
  // backstop against any double-render.
  //
  // Two guards keep non-pages out (see Tracking/utils/trackablePath.ts):
  //   1. isNotFound   — Gatsby served the 404 page for this URL.
  //   2. isProbePath  — the URL cannot be a route here at all (.php/.aspx/
  //      dot-segments). Stateless, so it holds even if the 404 marker is ever
  //      hidden by a framework wrapper change.
  // Both fail open: if neither matches, behaviour is exactly as before.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = location.pathname;
      if (isNotFound || isProbePath(raw)) return;
      const route = toRoute(raw);
      emit(TraceTokEvents.PageViewed, {
        page_name: derivePageName(route),
        page_group: derivePageGroup(route),
      });
    } catch {
      // never break the app on analytics
    }
  }, [location.pathname, isNotFound, emit]);

  // P1: deeplink_opened — fires once on app entry if utm/campaign params present
  const deeplinkFiredRef = useRef(false);
  useEffect(() => {
    if (deeplinkFiredRef.current) return;
    if (typeof window === 'undefined') return;
    // Same guards as page_viewed above: a scanner hitting /search.php?id=1
    // resolves to the 404 page but still carries a query string, which would
    // otherwise be counted as a deeplink. deeplink_opened rose to 80,558 on
    // 5 Aug 2026 with no matching install increase.
    if (isNotFound || isProbePath(location.pathname)) return;

    // Only a genuine campaign/deeplink parameter counts. The original
    // `(params.toString() ? 'query' : undefined)` fallback fired this event for
    // ANY query string — `?fbclid=`, `?gclid=`, anything a bot appended — with
    // campaign_id left empty. Production confirms the scale: on 5 Aug 2026,
    // 80,491 of 80,558 events (99.9%) carried the literal value "query", and
    // campaign_id was empty on 99.6% of the week. Present since the event was
    // introduced (ed7857ef, 21 May 2026).
    //
    // The allow-list includes `pid`/`c` because those are what this app's own
    // OneLink builder emits; without them real AppsFlyer campaigns would emit
    // nothing. firstParam() ignores present-but-empty values (`?pid=`), which
    // are not a campaign.
    //
    // NOTE: this permanently lowers deeplink_opened on ordinary days too —
    // "query" was 36–95% of the event depending on campaign activity.
    // Acknowledged by the data team.
    const params = new URLSearchParams(location.search);
    const campaignId = firstParam(params, CAMPAIGN_PARAMS);
    const deeplinkType = firstParam(params, SOURCE_PARAMS);

    if (campaignId || deeplinkType) {
      deeplinkFiredRef.current = true;
      emit(TraceTokEvents.DeeplinkOpened, {
        campaign_id: campaignId,
        deeplink_type: deeplinkType,
      });
    }
    // Entry-scoped by design (deeplinkFiredRef): this reports the campaign the
    // visit arrived on, not every subsequent in-app URL. `emit` is stable, so
    // this runs once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emit]);

  // P1: identify() when the member becomes available, traits pushed once
  // per identity change so Amplitude / Braze / backend stay in sync.
  const lastIdentifiedRef = useRef<string | undefined>();
  useEffect(() => {
    const memberId = member?._id || member?.memberDatasId;
    if (!memberId || lastIdentifiedRef.current === memberId) return;
    lastIdentifiedRef.current = memberId;
    identify(memberId, {
      email: member?.email,
      first_name: (member as any)?.firstName,
      last_name: (member as any)?.lastName,
      country: (member as any)?.country,
      language: (member as any)?.language,
      subscription_type: (member as any)?.memberSubscription?.productId,
    });
    // Visible companion event: Amplitude's $identify sets user properties but is
    // not shown as a normal event, so emit a countable "identify" alongside it.
    // Via the module bridge so it fires regardless of chunk.
    trackEvent(TraceTokEvents.Identify, {user_id: memberId});
    // If the member just signed up, emit account_info_submitted HERE — after the
    // user_id is set — so it is no longer anonymous. The signup flow only sets
    // this flag on a real signup, so regular logins do not fire it.
    if (consumeSignupPending()) {
      trackEvent(TraceTokEvents.AccountInfoSubmitted, {fields_group: 'signup'});
    }
  }, [member, identify]);

  useEffect(() => {
    if (!isModal) {
      PreviousLayout.current = getElementLayout(element);
      PreviousElement.current = element;
    }
  }, [element, isModal]);

  // Layout component
  const LayoutName = useMemo(
    () => (isModal ? PreviousLayout.current : getElementLayout(element)),
    [isModal, element],
  );

  const Layout = layouts[LayoutName ?? 'DefaultLayout'];

  return (
    <div className="layout-root">
      <TranslationLoader language={pageContext.language} />
      <Suspense fallback={null}>
        <Layout {...{context: wrapperProps.pageContext}}>{PageElement}</Layout>
      </Suspense>
      {isModal && (
        <Modal
          isOpen={isModal}
          onClose={handleModalClose}
          // @ts-ignore
          {...element.modalProps}>
          <ModalRoutingContext.Provider
            value={{
              modal: true,
              closeTo,
            }}>
            {/* {ModalLayout ? (
              <ModalLayout variant="modal">{modalElement}</ModalLayout>
            ) : ( */}
            {LayoutName === 'AwardLayout' ? (
              <Suspense fallback={null}>
                <Layout
                  {...{context: wrapperProps.pageContext}}
                  variant="modal">
                  {modalElement}
                </Layout>
              </Suspense>
            ) : (
              modalElement
            )}
            {/* )} */}
          </ModalRoutingContext.Provider>
        </Modal>
      )}
      {isModal && (
        <style
          id="body-override"
          dangerouslySetInnerHTML={{
            __html: `
              body {
                overflow: hidden !important;     
              }`,
          }}></style>
      )}
      <Suspense>
        <ThankYouModal />
      </Suspense>
      <Suspense>
        <SessionAlertProvider />
      </Suspense>
    </div>
  );
};

export default LayoutProvider;
